/**
 * 메모 쓰기 · 수정 · 지우기(요청서 N1~N3) — 학생 상세 메모 탭 · 반 관리의 반 메모가 같이 쓴다.
 * 매니저는 모든 메모를 고치고 지운다. 강사는 범위 학생 · 본인 담임 반 메모를 쓰고, 본인 메모만 고치고 지운다(요청서 §10).
 * 지우기가 거절되면(403 「본인 메모만 …」 등) 그 줄 아래에 서버 문구를 보인다(백엔드 8단계 전달 §4-E).
 * 목록은 고정 먼저, 그다음 일어난 날 최신순(서버 정렬).
 */
import React, { useState } from 'react';
import { AcademyButton, ActionButton, compactInputBase, saveIcon } from '../controls';
import { Pin } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { createNote, deleteNote, listAcademyNotes, listNotes, updateNote } from '../academyApi';
import { todayKST } from '../periodDates';
import { Box, ErrorNotice, Spinner, ToneBadge, notifyAcademyChanged, useAcademyVersion, useAsync } from '../ui';
import { DateInput, EmptyCodesNote, ErrorBox, TextArea, useCodeList } from '../modals/form';
import NoteOption from './NoteOption';

function NoteForm({ initial, onSave, onCancel, saveLabel = '저장' }) {
  const catList = useCodeList('note_category');
  const cats = catList.items;
  const [f, setF] = useState(initial || { categoryCode: '', occurredOn: todayKST(), body: '', isIssue: false, isPinned: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const category = f.categoryCode || cats[0]?.code || '';
  const save = async () => {
    setBusy(true); setError(null);
    try { await onSave({ ...f, categoryCode: category }); if (!initial) setF({ ...f, body: '', isIssue: false, isPinned: false }); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="메모 분류" value={category} onChange={(e) => setF({ ...f, categoryCode: e.target.value })} className={`${compactInputBase} w-28`}>
          {cats.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <DateInput aria-label="일어난 날" value={f.occurredOn} max={todayKST()} onChange={(v) => setF({ ...f, occurredOn: v })} compact fullWidth={false} className="w-36" />
        <NoteOption label="주요 이슈(캘린더)" description="중요한 메모로, 캘린더에 표시합니다." checked={f.isIssue} onChange={(isIssue) => setF({ ...f, isIssue })} />
        <NoteOption label="고정" description="이 메모를 목록 위쪽에 먼저 표시합니다." checked={f.isPinned} onChange={(isPinned) => setF({ ...f, isPinned })} />
      </div>
      {/* 메모 분류는 저장에 필수 — 고를 분류가 없어 [저장]이 잠길 때 그 까닭을 여기서 말한다(백엔드 0단계 전달 §4-D).
          수정 창은 메모의 원래 분류를 그대로 들고 있어(category) 목록이 비어도 저장된다 — 그때는 말하지 않는다 */}
      {!category && <EmptyCodesNote list={catList} label="메모 분류" required />}
      <TextArea aria-label="메모 내용" value={f.body} rows={3} onChange={(v) => setF({ ...f, body: v })} placeholder="메모 내용" />
      <ErrorBox error={error} />
      <div className="flex justify-end gap-2">
        {onCancel && <AcademyButton type="button" onClick={onCancel} variant="secondary">취소</AcademyButton>}
        <AcademyButton icon={saveIcon} disabled={busy || !f.body.trim() || !category} loading={busy} onClick={save} variant="primary">{busy ? '저장 중…' : saveLabel}</AcademyButton>
      </div>
    </div>
  );
}

/**
 * @param target  { studentId } | { classId } | { academy: true } | { allAcademy: true }
 * @param canWrite  쓰기 칸을 그릴지
 * @param canEdit(note)  그 메모를 고치고 지울 수 있는지
 */
function NotesPanel({ target, canWrite = true, canEdit = () => true, title = '메모', sub = '', limit = null, page = 1, onPageChange, from, to, collapsibleComposer = false, composeTitle = '새 메모' }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const key = JSON.stringify(target);
  const notes = useAsync((signal) => (target.allAcademy ? listAcademyNotes : listNotes)({ ...target, from, to, page, signal }, getAccessToken), `notes-${key}-${from || ''}-${to || ''}-${page}-${version}`);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removeError, setRemoveError] = useState(null); // { id, error } — 지우기에 실패한 메모와 그 까닭
  const canCreate = canWrite && !target.allAcademy;
  const create = async (f) => {
    await createNote({ studentId: target.studentId, classId: target.classId, categoryCode: f.categoryCode, body: f.body, occurredOn: f.occurredOn, isIssue: f.isIssue, isPinned: f.isPinned }, getAccessToken);
    if (collapsibleComposer) setComposing(false);
    notifyAcademyChanged();
  };
  const update = async (id, f) => { await updateNote(id, f, getAccessToken); setEditing(null); notifyAcademyChanged(); };
  const remove = async (id) => {
    // eslint-disable-next-line no-alert -- 지우기 전 한 번 묻는다(저장소의 다른 지우기와 같은 방식)
    if (!window.confirm('이 메모를 삭제할까요?')) return;
    setRemoveError(null);
    try {
      await deleteNote(id, getAccessToken);
      notifyAcademyChanged();
    } catch (e) {
      setRemoveError({ id, error: e });
    }
  };
  const items = notes.data?.items || [];
  const shown = limit ? items.slice(0, limit) : items;
  return (
    <Box title={title} sub={sub} right={canCreate && collapsibleComposer && !composing && <AcademyButton onClick={() => setComposing(true)}>메모 쓰기</AcademyButton>}>
      {target.allAcademy && <p className="mb-2 text-xs text-gray-500">메모를 쓰려면 위에서 학원 또는 반을 선택하세요.</p>}
      {canCreate && (!collapsibleComposer || composing) && <div role={collapsibleComposer ? 'region' : undefined} aria-label={collapsibleComposer ? '새 메모 작성' : undefined} className="mb-3 space-y-2 rounded-xl bg-gray-50 p-2">
        {collapsibleComposer && <p className="text-xs font-semibold text-gray-900">{composeTitle}</p>}
        <NoteForm onSave={create} onCancel={collapsibleComposer ? () => setComposing(false) : undefined} />
      </div>}
      {notes.loading && !notes.data && <Spinner label="메모 불러오는 중" />}
      {notes.error && <ErrorNotice error={notes.error} onRetry={notes.reload} compact />}
      {notes.data && shown.length === 0 && <p className="py-2 text-xs text-gray-500">{from || to ? '조회 기간에 메모가 없습니다.' : '메모가 없습니다.'}</p>}
      <ul aria-label="메모 목록" className="divide-y divide-gray-100">
        {shown.map((n) => (
          <li key={n.id} className="py-2 text-xs">
            {editing === n.id ? (
              <NoteForm
                saveLabel="수정 저장"
                initial={{ categoryCode: n.category_code, occurredOn: n.occurred_on || todayKST(), body: n.body, isIssue: n.is_issue, isPinned: n.is_pinned }}
                onSave={(f) => update(n.id, f)}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-mono text-gray-500">{n.occurred_on ? n.occurred_on.slice(5).replace('-', '/') : '—'}</span>
                <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-gray-900">
                  {n.is_pinned && <Pin aria-label="고정" className="mr-1 inline size-3 text-primary" />}
                  {n.category && <span className="text-gray-500">{n.category} · </span>}
                  {!target.classId && (n.class_name || target.allAcademy) && <span className="text-gray-500">{n.class_name || '학원'} · </span>}
                  {n.body}
                  {n.is_issue && <span className="ml-1 align-middle"><ToneBadge tone="danger">주요 이슈</ToneBadge></span>}
                </p>
                <span className="shrink-0 text-gray-400">{n.author_name}</span>
                {canEdit(n) && (
                  <span className="flex shrink-0 gap-1">
                    <ActionButton iconOnly type="button" onClick={() => setEditing(n.id)}>수정</ActionButton>
                    <ActionButton iconOnly type="button" onClick={() => remove(n.id)} danger>지우기</ActionButton>
                  </span>
                )}
              </div>
            )}
            {removeError?.id === n.id && <div className="mt-1"><ErrorBox error={removeError.error} /></div>}
          </li>
        ))}
      </ul>
      {onPageChange && notes.data && (notes.data.totalPages > 1 || page > 1) && <div className="mt-3 flex items-center justify-end gap-2 text-xs text-gray-500">
        <AcademyButton disabled={page <= 1 || notes.loading} onClick={() => onPageChange(page - 1)}>이전</AcademyButton>
        <span>{page} / {notes.data.totalPages}</span>
        <AcademyButton disabled={page >= notes.data.totalPages || notes.loading} onClick={() => onPageChange(page + 1)}>다음</AcademyButton>
      </div>}
    </Box>
  );
}

export default NotesPanel;
